/**
 * OverridableFieldWrapper — context-aware wrapper for GLOBAL override fields.
 *
 * Rendering rules:
 * - Global context + isOverridable: renders children normally
 * - Global context + isCompanyOnly: returns null (hidden)
 * - Global context + non-overridable: renders children normally
 * - Company context + non-overridable (not isCompanyOnly): shows disabled overlay
 * - Company context + isOverridable: renders children + ↺ revert button + optional global hint
 * - Company context + isCompanyOnly: renders children normally (no revert)
 *
 * Revert button placement:
 * - Selection types (boolean): button rendered inline, immediately right of the component
 * - Text-entry types (input, select, textarea, …): button rendered inside the input on the right
 *
 * @module components/form/fields/OverridableFieldWrapper
 * @see FMPV-MVWC-E004-S004 I-002
 */
import React from 'react';
import { RotateCcw, Globe } from 'lucide-react';
import type { MasterPatternViewInputType } from '../../../types/definition.types';

/** Field types whose value is chosen, not typed — revert btn goes beside them */
const SELECTION_TYPES = new Set<MasterPatternViewInputType>(['boolean']);

export interface OverridableFieldWrapperProps {
  fieldName: string;
  isOverridable: boolean;
  isCompanyOnly: boolean;
  context: 'global' | 'company';
  globalValue: unknown;
  isShowGlobalValuesEnabled: boolean;
  onRevert: (fieldName: string, globalValue: unknown) => void;
  children: React.ReactNode;
  /** Resolved input type — used to decide revert button placement */
  fieldType: MasterPatternViewInputType;
  /** Grid cell class (e.g. col-span-6) — must be set when this wrapper is a direct grid child */
  className?: string;
}

export function OverridableFieldWrapper({
  fieldName,
  isOverridable,
  isCompanyOnly,
  context,
  globalValue,
  isShowGlobalValuesEnabled,
  onRevert,
  children,
  fieldType,
  className = '',
}: OverridableFieldWrapperProps): React.ReactElement | null {
  // isCompanyOnly fields are hidden in global context (no global row)
  if (isCompanyOnly && context === 'global') {
    return null;
  }

  // Company context + non-overridable (and not isCompanyOnly): show disabled
  if (context === 'company' && !isOverridable && !isCompanyOnly) {
    return (
      <div data-testid="override-field-disabled" className={`relative opacity-50 pointer-events-none ${className}`.trim()}>
        {children}
      </div>
    );
  }

  // Company context + isOverridable: children + ↺ button + optional global hint
  if (context === 'company' && isOverridable) {
    const isSelection = SELECTION_TYPES.has(fieldType);

    const revertBtn = (
      <button
        data-testid="override-revert-button"
        type="button"
        title="Revertir al valor global"
        className="flex-shrink-0 text-content-tertiary dark:text-dark-content-tertiary hover:text-content-primary dark:hover:text-dark-content-primary cursor-pointer"
        onClick={() => onRevert(fieldName, globalValue)}
      >
        <RotateCcw size={14} />
      </button>
    );

    const hint = isShowGlobalValuesEnabled && globalValue !== undefined && (
      <span
        data-testid="override-global-value-hint"
        className="flex items-center gap-1 text-[12px] text-[#1355b7] italic"
        style={{ marginTop: 2 }}
      >
        <Globe size={11} className="flex-shrink-0 text-[#1355b7]" />
        Valor global: <strong className="font-bold">{String(globalValue)}</strong>
      </span>
    );

    if (isSelection) {
      // Button sits immediately to the right of the component, aligned at the bottom
      // (items-end so the button sits level with the control, not the label)
      return (
        <div className={`flex flex-col gap-1 ${className}`.trim()}>
          <div className="flex items-end gap-2">
            <div>{children}</div>
            <div className="mb-0.5">{revertBtn}</div>
          </div>
          {hint}
        </div>
      );
    }

    // Text-entry types: button is absolutely positioned inside the input on the right.
    // [&_input]:pr-8 adds right padding to native <input> so text doesn't slide under the icon.
    return (
      <div className={`flex flex-col gap-1 ${className}`.trim()}>
        <div className="relative [&_input]:pr-8">
          {children}
          <div className="absolute right-2 top-[46px] -translate-y-1/2">
            {revertBtn}
          </div>
        </div>
        {hint}
      </div>
    );
  }

  // Global context + isOverridable: render children normally (no extra hint)
  if (context === 'global' && isOverridable) {
    return <>{children}</>;
  }

  // Default: render children as-is (isCompanyOnly in company context, or normal global field)
  return <>{children}</>;
}
